import React, { useMemo } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

export interface SummaryToggleOption {
  key: string;
  internalDescription: string;
  internalDescriptionLong: string;
  isActive: boolean;
  isBeingDeprecated?: boolean;
}

interface Props {
  title: string;
  options: Array<SummaryToggleOption>;
  selectedKeys: Array<string>;
  onChange: (selectedKeys: Array<string>) => void;
  disabled?: boolean;
}

function getToggleLabel(option: SummaryToggleOption): string {
  return `${option.isBeingDeprecated ? '[DEPRECATED] - ' : ''}${!option.isActive ? '[INACTIVE] - ' : ''}${
    option.internalDescription
  }`;
}

function getToggleDescription(option: SummaryToggleOption): string {
  if (option.isActive) {
    return option.internalDescriptionLong;
  }

  return `Inactive - this toggle will not be applied. ${option.internalDescriptionLong}`;
}

export default function SummaryToggleAccordion({ title, options, selectedKeys, onChange, disabled = false }: Props) {
  const selectedKeySet = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const selectedOptions = useMemo<Array<SummaryToggleOption>>(
    () =>
      selectedKeys
        .map((key) => options.find((option) => option.key === key))
        .filter((option): option is SummaryToggleOption => Boolean(option)),
    [options, selectedKeys],
  );

  const handleToggle = (key: string, checked: boolean) => {
    if (checked) {
      onChange(selectedKeySet.has(key) ? selectedKeys : [...selectedKeys, key]);
      return;
    }

    onChange(selectedKeys.filter((selectedKey) => selectedKey !== key));
  };

  return (
    <Accordion disableGutters variant="outlined">
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={0.75} sx={{ minWidth: 0, width: '100%', pr: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">{title}</Typography>
            {selectedOptions.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {selectedOptions.length} selected
              </Typography>
            )}
          </Stack>
          {selectedOptions.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {selectedOptions.map((option) => (
                <Box
                  key={option.key}
                  component="span"
                  sx={{
                    bgcolor: 'action.selected',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    color: 'text.secondary',
                    fontSize: (theme) => theme.typography.caption.fontSize,
                    lineHeight: 1.35,
                    maxWidth: '100%',
                    px: 0.75,
                    py: 0.25,
                    overflowWrap: 'anywhere',
                    textDecoration: option.isBeingDeprecated ? 'line-through' : undefined,
                  }}
                >
                  {getToggleLabel(option)}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No toggles selected
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Stack spacing={1}>
          {options.map((option) => {
            const label = getToggleLabel(option);
            const description = getToggleDescription(option);

            return (
              <Box
                key={option.key}
                sx={{
                  border: 1,
                  borderColor: selectedKeySet.has(option.key) ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  textDecoration: option.isBeingDeprecated ? 'line-through' : undefined,
                }}
              >
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', m: 0 }}
                  control={
                    <Checkbox
                      checked={selectedKeySet.has(option.key)}
                      onChange={(event) => handleToggle(option.key, event.target.checked)}
                      disabled={disabled}
                      sx={{ mt: -0.5 }}
                    />
                  }
                  label={
                    <Stack spacing={0.5}>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {description}
                      </Typography>
                    </Stack>
                  }
                />
              </Box>
            );
          })}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
